<template>
  <!-- 这是一个无渲染组件，仅用于管理结构化数据 -->
</template>

<script setup>
import { computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  // 页面类型：'home', 'analyze', 'plant', 'about', 'contact', 'article'
  pageType: {
    type: String,
    required: true
  },
  // 页面标题
  title: {
    type: String,
    default: ''
  },
  // 页面描述
  description: {
    type: String,
    default: ''
  },
  // 页面图片URL
  imageUrl: {
    type: String,
    default: ''
  },
  // 植物数据（用于植物详情页）
  plantData: {
    type: Object,
    default: () => ({})
  },
  // 文章数据（用于文章页）
  articleData: {
    type: Object,
    default: () => ({})
  }
});

const route = useRoute();
const { t } = useI18n();

// 网站基础URL
const baseUrl = 'https://whisperherb.com';

// 当前页面的完整URL
const currentUrl = computed(() => {
  return `${baseUrl}${route.path}`;
});

// 网站名称
const siteName = computed(() => t('app.name'));

// 网站Logo
const siteLogo = computed(() => `${baseUrl}/icons/icon-512x512.png`);

// 根据页面类型生成不同的结构化数据
const generateStructuredData = () => {
  let structuredData;
  
  switch(props.pageType) {
    case 'home':
      structuredData = {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        'name': siteName.value,
        'url': baseUrl,
        'description': props.description || t('app.tagline'),
        'potentialAction': {
          '@type': 'SearchAction',
          'target': {
            '@type': 'EntryPoint',
            'urlTemplate': `${baseUrl}/analyze?q={search_term_string}`
          },
          'query-input': 'required name=search_term_string'
        }
      };
      break;
      
    case 'analyze':
      structuredData = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        'name': props.title || t('analyze.title'),
        'description': props.description || t('analyze.subtitle'),
        'url': currentUrl.value,
        'isPartOf': {
          '@type': 'WebSite',
          'name': siteName.value,
          'url': baseUrl
        },
        'primaryImageOfPage': {
          '@type': 'ImageObject',
          'url': props.imageUrl || `${baseUrl}/icons/analyze-feature.jpg`
        },
        'offers': {
          '@type': 'Offer',
          'price': '0',
          'priceCurrency': 'USD',
          'availability': 'https://schema.org/InStock'
        }
      };
      break;
      
    case 'plant':
      if (props.plantData && Object.keys(props.plantData).length > 0) {
        structuredData = {
          '@context': 'https://schema.org',
          '@type': 'Product',
          'name': props.plantData.name || 'Plant',
          'description': props.plantData.description || 'Plant details',
          'image': props.plantData.imageUrl || props.imageUrl || `${baseUrl}/icons/default-plant.jpg`,
          'url': currentUrl.value,
          'brand': {
            '@type': 'Brand',
            'name': siteName.value
          },
          'offers': {
            '@type': 'Offer',
            'price': '0',
            'priceCurrency': 'USD',
            'availability': 'https://schema.org/InStock',
            'url': currentUrl.value
          }
        };
      } else {
        structuredData = {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          'name': props.title || 'Plant Details',
          'description': props.description || 'Detailed information about this plant',
          'url': currentUrl.value
        };
      }
      break;
      
    case 'about':
      structuredData = {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        'name': props.title || t('about.title'),
        'description': props.description || t('about.subtitle'),
        'url': currentUrl.value,
        'isPartOf': {
          '@type': 'WebSite',
          'name': siteName.value,
          'url': baseUrl
        }
      };
      break;
      
    case 'contact':
      structuredData = {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        'name': props.title || t('contact.title'),
        'description': props.description || t('contact.subtitle'),
        'url': currentUrl.value,
        'isPartOf': {
          '@type': 'WebSite',
          'name': siteName.value,
          'url': baseUrl
        }
      };
      break;
      
    case 'article':
      if (props.articleData && Object.keys(props.articleData).length > 0) {
        structuredData = {
          '@context': 'https://schema.org',
          '@type': 'Article',
          'headline': props.articleData.title || props.title,
          'description': props.articleData.description || props.description,
          'image': props.articleData.imageUrl || props.imageUrl,
          'datePublished': props.articleData.publishDate,
          'dateModified': props.articleData.modifiedDate || props.articleData.publishDate,
          'author': {
            '@type': 'Person',
            'name': props.articleData.author || siteName.value
          },
          'publisher': {
            '@type': 'Organization',
            'name': siteName.value,
            'logo': {
              '@type': 'ImageObject',
              'url': siteLogo.value
            }
          },
          'mainEntityOfPage': {
            '@type': 'WebPage',
            '@id': currentUrl.value
          }
        };
      } else {
        structuredData = {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          'name': props.title,
          'description': props.description,
          'url': currentUrl.value
        };
      }
      break;
      
    default:
      structuredData = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        'name': props.title,
        'description': props.description,
        'url': currentUrl.value,
        'isPartOf': {
          '@type': 'WebSite',
          'name': siteName.value,
          'url': baseUrl
        }
      };
  }
  
  return structuredData;
};

// 添加结构化数据到页面
const addStructuredData = () => {
  // 移除之前的动态结构化数据
  const existingScripts = document.querySelectorAll('script[data-structured-data="true"]');
  existingScripts.forEach(script => script.remove());
  
  // 创建新的结构化数据脚本标签
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(generateStructuredData());
  script.setAttribute('data-structured-data', 'true');
  document.head.appendChild(script);
};

// 监听路由变化
watch(
  () => route.path,
  () => {
    addStructuredData();
  }
);

// 监听props变化
watch(
  () => [props.title, props.description, props.imageUrl, props.plantData, props.articleData],
  () => {
    addStructuredData();
  },
  { deep: true }
);

// 组件挂载时添加结构化数据
onMounted(() => {
  addStructuredData();
});
</script>